import config from '../config.js';

/**
 * Frontend side of country eligibility and the musician waitlist (ENG-375).
 *
 * SongShake opens country by country: a listener can use it from anywhere it
 * runs, but a performer can only be paid where the payout rails and the
 * paperwork are in place. So the country an account belongs to carries an
 * **eligibility state**, and where performer sign-up is not open yet the
 * register-performer page offers a **waitlist** instead of the form — the one
 * thing a musician in that country can usefully do, and the only signal we
 * have of how much demand a country holds. Everything the frontend expects of
 * the backend is pinned here for it to diff against:
 *
 * Endpoints (authenticated, under `config.apiBaseUrl`):
 *   GET /me/eligibility (ENG-374's `eligibility.GetMyEligibility`)
 *       → { status: 'supported' | 'unsupported' | 'blocked',
 *           reasonCode: string,
 *           countryCode?: 'GB',
 *           listener: { membershipsAvailable, donationsAvailable },
 *           musician: { status: 'active' | 'waitlist_required'
 *                               | 'waitlist_pending' | 'waitlist_rejected'
 *                               | 'location_unknown',
 *                       reasonCode?: string,
 *                       application?: { status, createdAt, … } } }
 *       The account's country as ISO 3166-1 alpha-2 and what it may do,
 *       which `readCountryEligibility` turns into the page's own
 *       `{ country, eligibility, waitlist }` (ENG-469). A missing
 *       `countryCode` (the backend cannot place the account) still carries
 *       an eligibility; the page then says "your country" rather than
 *       naming one.
 *   POST /me/musician-waitlist (ENG-374's `musicianWaitlist.Apply`, ENG-476)
 *       ← { artistName: string (required, ≤ 100 characters),
 *           genre: string (≤ 100), portfolioUrl: http(s) URL (≤ 512 bytes),
 *           message: string (≤ 2000) }
 *       → 201 / 200 with the application: { id, status, countryCode,
 *           createdAt, … }
 *       400 { error: 'invalid_application', field } → that field is wrong.
 *       409 { error } → one of three things, told apart by the code:
 *           `waitlist_application_exists` — already applied: joined;
 *           `waitlist_not_required` — this session may sign up after all;
 *           `waitlist_location_unknown` — the backend could not place the
 *           request this time, so it is worth trying again.
 *       The country is the backend's to decide, from the same policy
 *       decision as the GET, so the join does not send one. Nor does it send
 *       an address: we write to the account's own (`users.email`, from the
 *       SSO sign-in), which the backend already holds.
 *   404 / 501 → the backend has not shipped this: sign-up stays open.
 *   401 / 403 → the check failing, not the end of the session: both paths are
 *       in fetchWrapper's AUTO_LOGOUT_EXEMPT_PATHS, alongside the
 *       notification and locale-preference reads. The GET goes out on its own
 *       on every visit to the page, against a route a deployment may not
 *       serve (where a gateway in front of it answers 401 as readily as 404)
 *       or may gate by role; ejecting the musician to /login for that would
 *       be the lockout this feature exists to avoid, by another door.
 *
 * One thing the backend owns that this file cannot:
 *
 * - **The gate itself.** Withholding the form is presentational: it saves a
 *   musician a POST that was going to be refused and tells them why. The
 *   refusal is still the backend's — `POST /bands` must reject a closed
 *   country on its own, since this check fails open by design (below) and a
 *   client can always be made to skip it.
 *
 * Two rules the rest of the feature is built on:
 *
 * - **Only a clear "no" closes sign-up.** `performerSignupOpen` is false for
 *   exactly the two states the backend named as closed. A load that failed,
 *   an endpoint that does not exist, a state this client does not know and a
 *   signed-out visitor all leave the form where it was — a country rollout
 *   must not be able to lock musicians out of sign-up by going down.
 * - **`unsupported` offers no waitlist.** Waiting is only worth asking for
 *   where we mean to open; where we cannot pay performers at all, a join
 *   button would promise something nobody intends to keep.
 */
export const COUNTRY_ELIGIBILITY_PATH = '/me/eligibility';
export const MUSICIAN_WAITLIST_PATH = '/me/musician-waitlist';

/**
 * How long the page waits for the check before it gives up and offers the
 * form anyway.
 *
 * A check that fails moves the state machine; a check that *hangs* — a
 * stalled connection, a proxy sitting on the request, a captive portal that
 * accepts it and answers nothing — would not, and `fetch` has no timeout of
 * its own. Without a deadline the "only a clear no closes sign-up" rule
 * holds for a check that is down but not for one that never answers, which
 * is the commoner shape: the page would sit on "Checking…" for as long as
 * the musician was willing to look at it. Long enough that an ordinary slow
 * answer still lands before it (the alternative is the form flashing away
 * under someone), short enough to be a wait rather than a wall.
 */
export const COUNTRY_ELIGIBILITY_TIMEOUT_MS = 3000;

export const countryEligibilityEndpoint = () => `${config.apiBaseUrl}${COUNTRY_ELIGIBILITY_PATH}`;
export const musicianWaitlistEndpoint = () => `${config.apiBaseUrl}${MUSICIAN_WAITLIST_PATH}`;

/**
 * What the account's country may do, as the backend names it.
 *
 * `UNKNOWN` is the client's own state, never sent: nobody has said yet (the
 * load is in flight, it failed, the endpoint is not there, the visitor is
 * signed out) or the backend named a state this build does not know.
 */
export const ELIGIBILITY = Object.freeze({
  /** Performer sign-up is open here. */
  OPEN: 'open',
  /** Listening works; performer sign-up is coming, and the waitlist is how to hear about it. */
  WAITLIST: 'waitlist',
  /** Performers cannot be onboarded here, and no date is promised. */
  UNSUPPORTED: 'unsupported',
  /** Not answered (yet), or answered with something this build does not know. */
  UNKNOWN: 'unknown',
});

/**
 * Where the eligibility check stands, as `useCountryEligibility().status`.
 * `idle` is a signed-out visitor (nothing is asked); `ready` means the
 * backend answered; `unavailable` that it has not shipped the endpoint; and
 * `error` any other failure. Only `ready` can close sign-up.
 */
export const COUNTRY_ELIGIBILITY_STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
  UNAVAILABLE: 'unavailable',
});

/** Nobody has joined: what the page shows before a join and after a load that names no entry. */
export const NO_WAITLIST_ENTRY = Object.freeze({ joined: false, joinedAt: null });

/** What consumers see without an answer: sign-up open, no country named, no entry. */
export const UNKNOWN_ELIGIBILITY = Object.freeze({
  country: null,
  eligibility: ELIGIBILITY.UNKNOWN,
  waitlist: NO_WAITLIST_ENTRY,
});

/**
 * `gb` / ` Gb ` → `GB`; anything that is not a two-letter ISO 3166-1 alpha-2
 * code — a name, a dial code, `null` — → null, so the page falls back to
 * "your country" rather than showing a value nobody can read.
 */
export const normalizeCountryCode = (raw) => {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : null;
};

const trimmed = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);

/** `GET /me/eligibility`'s musician states that mean "not open here, but the waitlist is". */
const WAITLIST_MUSICIAN_STATUSES = Object.freeze(['waitlist_required', 'waitlist_pending', 'waitlist_rejected']);

/**
 * The page's eligibility from the backend's policy and musician states.
 *
 * - `blocked` is a country SongShake cannot serve at all: no form and no
 *   waitlist, so `unsupported`.
 * - `active` (a launch country, or an approved application) is `open`.
 * - The three waitlist states are `waitlist`.
 * - `location_unknown` — the backend could not place this request — and
 *   anything this build does not know stay `unknown`, which leaves the form
 *   up. The backend still refuses `POST /bands` for such a request; the page
 *   must not funnel a launch-country musician into the waitlist over a
 *   lookup that failed.
 */
const eligibilityFrom = (policyStatus, musicianStatus) => {
  if (policyStatus === 'blocked') return ELIGIBILITY.UNSUPPORTED;
  if (musicianStatus === 'active') return ELIGIBILITY.OPEN;
  if (WAITLIST_MUSICIAN_STATUSES.includes(musicianStatus)) return ELIGIBILITY.WAITLIST;
  return ELIGIBILITY.UNKNOWN;
};

/**
 * The waitlist entry a GET implies. Only a pending application is "on the
 * list": a rejected applicant may apply again (the backend reuses their
 * row), so they get the join form back, and an approved one is `open` and
 * never sees the card. `joinedAt` is when the application was made.
 */
const waitlistFrom = (musician) => {
  if (musician.status !== 'waitlist_pending') return NO_WAITLIST_ENTRY;
  const application = musician.application && typeof musician.application === 'object' ? musician.application : {};
  return Object.freeze({ joined: true, joinedAt: trimmed(application.createdAt) });
};

/**
 * `{ country, eligibility, waitlist }` from a `GET /me/eligibility`
 * response. A body that is not an object at all (an empty 204, a proxy's
 * string) reads as unknown, which leaves sign-up open.
 */
export const readCountryEligibility = (response) => {
  if (!response || typeof response !== 'object') return UNKNOWN_ELIGIBILITY;
  const musician = response.musician && typeof response.musician === 'object' ? response.musician : {};
  return Object.freeze({
    country: normalizeCountryCode(response.countryCode),
    eligibility: eligibilityFrom(response.status, musician.status),
    waitlist: waitlistFrom(musician),
  });
};

/** The application's fields, in the order the card asks for them. */
export const WAITLIST_FIELDS = Object.freeze(['artistName', 'genre', 'portfolioUrl', 'message']);

/**
 * The backend's limits (`musicianWaitlist.ApplicationInput.Validate`), so the
 * card can say what is wrong before the POST rather than after it. Text is
 * counted in code points, as the backend counts runes; the URL in bytes, as
 * the backend counts it.
 */
export const WAITLIST_LIMITS = Object.freeze({ artistName: 100, genre: 100, portfolioUrl: 512, message: 2000 });

/** The catalog message for a field the card or the backend found wrong. */
export const WAITLIST_FIELD_ERROR_KEYS = Object.freeze({
  artistName: 'registerBand.waitlist.errors.artistNameInvalid',
  genre: 'registerBand.waitlist.errors.genreInvalid',
  portfolioUrl: 'registerBand.waitlist.errors.portfolioUrlInvalid',
  message: 'registerBand.waitlist.errors.messageInvalid',
});

export const EMPTY_WAITLIST_APPLICATION = Object.freeze({ artistName: '', genre: '', portfolioUrl: '', message: '' });

const codePoints = (value) => [...value].length;
const utf8Bytes = (value) => new TextEncoder().encode(value).length;

const isHttpUrl = (value) => {
  try {
    const url = new URL(value);
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.host !== '';
  } catch {
    return false;
  }
};

/** The body of `POST /me/musician-waitlist`: every field, trimmed. */
export const serializeWaitlistJoin = (application = {}) =>
  Object.fromEntries(WAITLIST_FIELDS.map((field) => [field, String(application[field] ?? '').trim()]));

/**
 * The first field the backend would refuse, as `{ field, key }`, or null when
 * the application may be sent. The same rules, in the same order, as
 * `ApplicationInput.Validate`, so the card and the server never disagree
 * about which field to point at.
 */
export const validateWaitlistApplication = (application = {}) => {
  const body = serializeWaitlistJoin(application);
  const wrong = (field) => ({ field, key: WAITLIST_FIELD_ERROR_KEYS[field] });
  if (body.artistName === '' || codePoints(body.artistName) > WAITLIST_LIMITS.artistName) return wrong('artistName');
  if (codePoints(body.genre) > WAITLIST_LIMITS.genre) return wrong('genre');
  if (codePoints(body.message) > WAITLIST_LIMITS.message) return wrong('message');
  if (body.portfolioUrl !== '' && (utf8Bytes(body.portfolioUrl) > WAITLIST_LIMITS.portfolioUrl || !isHttpUrl(body.portfolioUrl))) {
    return wrong('portfolioUrl');
  }
  return null;
};

/**
 * The entry to show after a join went through. The backend answers with the
 * application, so `joinedAt` is when it was made; a 409 "already applied"
 * carries no body the page sees and leaves it null.
 *
 * `joinedAt` is only ever the backend's. An acknowledgement that carries no
 * stamp leaves it null and the card prints no date, rather than standing in
 * "now": for the 409 especially, "already on it" is precisely the case where
 * the join happened in another tab, on another device or months ago, and a
 * date is the one thing on that card that is checkable.
 */
export const readWaitlistJoin = (response) =>
  Object.freeze({
    joined: true,
    joinedAt: response && typeof response === 'object' ? trimmed(response.createdAt) : null,
  });

/** 404/501 from fetchWrapper: the endpoint does not exist on this deployment. */
export const isCountryEligibilityUnavailableError = (error) => [404, 501].includes(Number(error?.status));

/** The machine-readable codes `POST /me/musician-waitlist` refuses with. */
export const WAITLIST_ERROR_CODES = Object.freeze({
  INVALID: 'invalid_application',
  EXISTS: 'waitlist_application_exists',
  NOT_REQUIRED: 'waitlist_not_required',
  LOCATION_UNKNOWN: 'waitlist_location_unknown',
});

const isConflict = (error, code) => Number(error?.status) === 409 && error?.code === code;

/**
 * 409 `waitlist_application_exists`: this account has applied already, which
 * is what it wanted. Only that code — the backend answers 409 for two other
 * reasons that mean something else entirely (below).
 */
export const isAlreadyOnWaitlistError = (error) => isConflict(error, WAITLIST_ERROR_CODES.EXISTS);

/** 409 `waitlist_not_required`: this session may sign up as a performer after all. */
export const isWaitlistNotRequiredError = (error) => isConflict(error, WAITLIST_ERROR_CODES.NOT_REQUIRED);

/** 409 `waitlist_location_unknown`: the backend could not place the request; worth another try. */
export const isWaitlistLocationUnknownError = (error) => isConflict(error, WAITLIST_ERROR_CODES.LOCATION_UNKNOWN);

/** The field a 400 `invalid_application` names, when it is one the card has. */
export const waitlistErrorField = (error) =>
  Number(error?.status) === 400 && WAITLIST_FIELDS.includes(error?.field) ? error.field : null;

/**
 * Whether the register-performer form is offered. Closed only for the states
 * the backend named as closed; see the file comment.
 */
export const performerSignupOpen = (eligibility) =>
  eligibility !== ELIGIBILITY.WAITLIST && eligibility !== ELIGIBILITY.UNSUPPORTED;

/**
 * Whether there is a waitlist to join — `waitlist` only, never `unsupported`.
 * The card branches on this rather than on the state name, so the rule above
 * is written down once.
 */
export const waitlistOffered = (eligibility) => eligibility === ELIGIBILITY.WAITLIST;
